import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  AccessCode,
  AccessCodeStatus,
  NewAccessCodes,
  createAccessCodes,
  fetchAccessCodes,
  revokeAccessCode,
} from '../../api/tenantAdmin';
import './TenantAccessCodesPage.css';

/**
 * Access codes, for a partner who pays for their customers.
 *
 * Each customer gets one six-digit code and enters it on the partner's site
 * instead of paying. A code works once. The plain codes are shown exactly
 * once, right after they are made: only a hash is stored, so a lost code
 * cannot be looked up, only withdrawn and replaced.
 *
 * Every member sees the list; making and withdrawing codes is for admins,
 * which the backend enforces too.
 */

const STATUS: Record<AccessCodeStatus, string> = {
  open: 'Not used yet',
  claimed: 'Entered, application started',
  used: 'Used',
  revoked: 'Withdrawn',
  expired: 'Expired',
};

const VALIDITY = [7, 30, 90];

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const TenantAccessCodesPage: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const { currentUser } = useAuth();
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [count, setCount] = useState(1);
  const [label, setLabel] = useState('');
  const [validDays, setValidDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState<NewAccessCodes | null>(null);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const data = await fetchAccessCodes(token);
      setCodes(Array.isArray(data?.codes) ? data.codes : []);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Could not load your access codes.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const make = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    setBusy(true);
    setError('');
    setCopied('');
    try {
      const token = await currentUser.getIdToken();
      const made = await createAccessCodes(token, { count, label: label.trim() || undefined, validDays });
      setFresh(made);
      setLabel('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not make the codes.');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (code: AccessCode) => {
    if (!currentUser) return;
    if (!window.confirm('Withdraw this code? Nobody can use it afterwards.')) return;
    setError('');
    try {
      const token = await currentUser.getIdToken();
      await revokeAccessCode(token, code.id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not withdraw the code.');
    }
  };

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch {
      setCopied('');
    }
  };

  if (loading) return <div className="tenant-codes"><p>Loading…</p></div>;

  const open = codes.filter(c => c.status === 'open').length;

  return (
    <div className="tenant-codes">
      <h2>Access codes</h2>
      <p className="tenant-codes-intro">
        Give each customer one code. They enter it on your site instead of paying, and each code
        works once. {open === 1 ? '1 code is' : `${open} codes are`} not used yet.
      </p>

      {error && <div className="tenant-codes-error" role="alert">{error}</div>}

      {isAdmin && (
        <form className="tenant-codes-form" onSubmit={make}>
          <label>
            <span>How many</span>
            <input
              type="number"
              min={1}
              max={50}
              value={count}
              onChange={e => setCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            />
          </label>
          <label className="tenant-codes-label">
            <span>Note (optional)</span>
            <input
              type="text"
              maxLength={120}
              placeholder="For example the customer's name"
              value={label}
              onChange={e => setLabel(e.target.value)}
            />
          </label>
          <label>
            <span>Valid for</span>
            <select value={validDays} onChange={e => setValidDays(Number(e.target.value))}>
              {VALIDITY.map(d => (
                <option key={d} value={d}>{d} days</option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={busy}>{busy ? 'Making…' : count === 1 ? 'Make a code' : `Make ${count} codes`}</button>
        </form>
      )}

      {fresh && (
        <div className="tenant-codes-fresh" role="status">
          <p>
            <strong>Copy {fresh.codes.length === 1 ? 'this code' : 'these codes'} now.</strong> You
            cannot see {fresh.codes.length === 1 ? 'it' : 'them'} again. Valid until {day(fresh.expiresAt)}.
          </p>
          <ul>
            {fresh.codes.map(c => (
              <li key={c.id}>
                <code>{c.code}</code>
                <button type="button" onClick={() => copy(c.code, c.id)}>{copied === c.id ? 'Copied' : 'Copy'}</button>
              </li>
            ))}
          </ul>
          {fresh.codes.length > 1 && (
            <button type="button" className="tenant-codes-copy-all" onClick={() => copy(fresh.codes.map(c => c.code).join('\n'), 'all')}>
              {copied === 'all' ? 'All copied' : 'Copy all'}
            </button>
          )}
          <button type="button" className="tenant-codes-done" onClick={() => setFresh(null)}>Done, I copied them</button>
        </div>
      )}

      {codes.length === 0 ? (
        <p className="tenant-codes-empty">No codes yet.{isAdmin ? ' Make one above.' : ' An admin of your organisation can make them.'}</p>
      ) : (
        <table className="tenant-codes-table">
          <thead>
            <tr>
              <th>Note</th>
              <th>Status</th>
              <th>Made</th>
              <th>Valid until</th>
              <th>Used</th>
              {isAdmin && <th aria-label="Actions"></th>}
            </tr>
          </thead>
          <tbody>
            {codes.map(c => (
              <tr key={c.id} className={`code-row-${c.status}`}>
                <td>{c.label || <span className="tenant-codes-muted">No note</span>}</td>
                <td><span className={`tenant-codes-status ${c.status}`}>{STATUS[c.status] ?? c.status}</span></td>
                <td>{day(c.createdAt)}{c.createdBy ? <small>{c.createdBy}</small> : null}</td>
                <td>{day(c.expiresAt)}</td>
                <td>{day(c.usedAt)}</td>
                {isAdmin && (
                  <td>
                    {c.status === 'open' && (
                      <button type="button" className="tenant-codes-revoke" onClick={() => withdraw(c)}>Withdraw</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TenantAccessCodesPage;
